/**
 * AdoraCare -- pure presentational screens for the PATIENT / TABLET intake
 * mode (PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1 Section 8, recomposed by
 * PATIENT-INTAKE-TABLET-RECOMPOSITION-V1 per founder findings T-1..T-7). No
 * hooks, no IO -- SSR-renderable (the repo idiom: no jsdom) so every render
 * is test-pinned.
 *
 * These carry ONLY the active intake's own content: the S-6 live stage
 * checklist (T-2), the compact details CARD with label: value rows +
 * per-field Fix + the one-line policy disclosure (T-1), the one-question
 * section shell (T-3), and the terminal hand-back copy. They import no
 * navigation, no lists, no other-patient data (Section 8).
 */

import type { ReactNode } from 'react';

import type { IntakeConsentPosture } from '@/dashboard/api';
import {
  PATIENT_MODE_HANDBACK_BODY,
  PATIENT_MODE_HANDBACK_TITLE,
  PATIENT_MODE_REVIEW_BODY,
  PATIENT_MODE_REVIEW_TITLE,
  fieldLabel,
  type PrefillReviewRow,
} from './patient-intake-mode-view';

/** The terminal hand-back screen (Section 8: "Please return this device to the
 *  receptionist"). Clean success vs staff-review-required. */
export function HandbackScreen({
  reviewRequired,
  checkedIn,
}: {
  readonly reviewRequired: boolean;
  readonly checkedIn: boolean;
}): JSX.Element {
  if (reviewRequired) {
    return (
      <div className="pim-card pim-handback" data-testid="patient-intake-handback-review">
        <h1>{PATIENT_MODE_REVIEW_TITLE}</h1>
        <p>{PATIENT_MODE_REVIEW_BODY}</p>
      </div>
    );
  }
  return (
    <div className="pim-card pim-handback" data-testid="patient-intake-handback">
      <h1>{PATIENT_MODE_HANDBACK_TITLE}</h1>
      <p>{PATIENT_MODE_HANDBACK_BODY}</p>
      {checkedIn ? <p data-testid="patient-intake-checked-in">You are checked in.</p> : null}
    </div>
  );
}

// (T-2's three-stage checklist RETIRED by AUG-12-WALK-2-REGISTRATION-TRUTH-V1
// F6: since the F3 reshape this form IS the registration -- the strip's
// "Registration - Reception - done" claim stopped being true, and the
// density pass wants one compact header. No dead exports kept (#246).)

// --- T-3: the one-question section shell ------------------------------------

/** One question per section (T-3): the question title + its chips/field,
 *  zero surrounding prose. `answered` renders the visible answered chip;
 *  `missing` renders the beside-the-field marker (T-4 / S-8) -- the host
 *  raises it only after a submit attempt, never pre-emptively. */
export function IntakeQuestionSection({
  id,
  title,
  required,
  answered,
  missing,
  hint,
  testId,
  fieldKey,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly required?: boolean;
  readonly answered?: boolean;
  readonly missing?: boolean;
  /** At most ONE short line (the phone-on-file ask, the patient-reported
   *  truth line) -- never a paragraph. */
  readonly hint?: string | null;
  readonly testId?: string;
  /** REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1 (D2): the CANONICAL
   *  registration key this section is the ONE control for. Stamped as
   *  `data-reg-field` so the generic completability test can count controls
   *  per required key without knowing a single field name. Absent on
   *  sections that are not registration fields (clinical questions, etc). */
  readonly fieldKey?: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section
      id={id}
      className="pim-card pim-qcard"
      {...(testId ? { 'data-testid': testId } : {})}
      {...(answered ? { 'data-answered': 'true' } : {})}
      {...(fieldKey ? { 'data-reg-field': fieldKey } : {})}
    >
      <h2 className="pim-q">
        {title}
        {required ? (
          <span className="pim-req" aria-hidden="true">
            *
          </span>
        ) : null}
        {answered ? <span className="pim-done-chip">Answered</span> : null}
        {missing ? <span className="pim-missing-pill">Not answered</span> : null}
      </h2>
      {hint ? <p className="pim-hint">{hint}</p> : null}
      {children}
    </section>
  );
}

// --- T-4/T-5: the anchored submit bar ---------------------------------------

/** The ANCHORED submit bar (T-4/T-5): opaque surface, top border, sticky
 *  bottom (the .pim-foot block). The verbatim server error (LES-009) and the
 *  minor stop live HERE -- never floating mid-form -- and the ONE primary
 *  action never overlaps content because the bar is opaque and content
 *  scrolls beneath it.
 *
 *  AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F6; founder walk W-5/W-8): the
 *  separate remaining-count line is GONE -- the disabled submit button
 *  carries the count in its own label (submitLabelForMissing), so the bar
 *  never shows two competing signals. */
export function IntakeSubmitBar({
  error,
  minorNote,
  submitButton,
  summaryLine = null,
}: {
  /** The server error, rendered VERBATIM (LES-009). */
  readonly error: string | null;
  readonly minorNote: string | null;
  readonly submitButton: ReactNode;
  /** FD-41: the pre-submit "your doctor will receive" line; null = nothing. */
  readonly summaryLine?: string | null;
}): JSX.Element {
  return (
    <div className="pim-foot" data-testid="intake-submit-bar">
      {summaryLine !== null ? (
        <p className="pim-hint" data-testid="intake-presubmit-summary">
          {summaryLine}
        </p>
      ) : null}
      {error ? (
        <p className="pim-error" data-testid="intake-submit-error">
          {error}
        </p>
      ) : null}
      {minorNote ? <p className="pim-minor">{minorNote}</p> : null}
      <div className="pim-foot__acts">{submitButton}</div>
    </div>
  );
}

// --- T-1: the compact details card ------------------------------------------

export interface PatientDetailsCardProps {
  readonly rows: readonly PrefillReviewRow[];
  /** WALK-3 (C2): the card heading is voice-supplied so the SAME card serves
   *  the patient ("Your details") and the desk ("Patient details"). Defaults
   *  to the patient copy, so every existing caller renders byte-identically. */
  readonly heading?: string;
  /** Known age text, or NULL -- the age section below asks; "not on file"
   *  never renders to the patient (F8). */
  readonly ageText: string | null;
  /** F4 (AUG-12-WALK-2-REGISTRATION-TRUTH-V1): the phone row's WHOLE cell is
   *  host-composed -- the person's own number in full, tap-to-edit inline;
   *  the separate mobile section is retired. Null keeps the presence-only
   *  fallback for older hosts. */
  readonly phoneCell?: ReactNode;
  /** F1: the pending proposed-changes banner (resume confirm surface). */
  readonly pendingBanner?: ReactNode;
  /** Fields whose Fix editor is open (the bounded-correction state). */
  readonly openFixFields: ReadonlySet<string>;
  readonly busy?: boolean;
  readonly onToggleFix: (field: string) => void;
  /** Host-supplied bounded correction editor for one open field. */
  readonly fixEditor: (row: PrefillReviewRow) => ReactNode;
}

/**
 * The prefill row field -> CANONICAL registration key map (D2). The prefill
 * wire calls the phone row `phone`; the tenant config token is `mobile`. This
 * map is the ONE place that translation lives -- and it is a MAP, never a
 * rename: neither the wire nor the config token changes (KEY-STABILITY; the
 * mismatch itself is recorded as a PARKED migration candidate in
 * docs/design/registration/REGISTRATION-CONTRACT.md).
 */
const REVIEW_ROW_FIELD_KEY: Readonly<Record<string, string>> = Object.freeze({
  full_name: 'full_name',
  phone: 'mobile',
});

/** The details CARD (T-1, recomposed by AUG-12-WALK-BLOCKERS-V1 F5/F8/F9 and
 *  AUG-12-WALK-2-REGISTRATION-TRUTH-V1 F4/F5): label: value rows in large
 *  type; every editable value is TAP-TO-EDIT IN PLACE (the value itself is
 *  the affordance; the Fix button remains as the labeled door). The phone
 *  row is host-composed (full own number + inline editor). */
export function PatientDetailsCard({
  rows,
  ageText,
  phoneCell = null,
  pendingBanner = null,
  heading = 'Your details',
  openFixFields,
  busy,
  onToggleFix,
  fixEditor,
}: PatientDetailsCardProps): JSX.Element {
  return (
    <section
      id="pim-sec-details"
      className="pim-card pim-details"
      data-testid="patient-intake-summary"
      aria-label="Details on file"
    >
      <h2 className="pim-q">{heading}</h2>
      {pendingBanner}
      <div className="pim-review">
        {rows.map((r) => {
          const fixable = r.identitySensitive && !r.isPhone;
          const open = openFixFields.has(r.field);
          return (
            <div key={r.field}>
              <div
                className="pim-review__row"
                data-testid={`intake-detail-${r.field}`}
                /* REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1 (D2): the
                   COMPLETABILITY marker. Every canonical registration key
                   renders exactly ONE element carrying data-reg-field, and
                   the CI test counts them against the tenant's required set.
                   The card owns two of them -- the name row and the
                   host-composed phone cell. */
                {...(REVIEW_ROW_FIELD_KEY[r.field] !== undefined
                  ? { 'data-reg-field': REVIEW_ROW_FIELD_KEY[r.field] }
                  : {})}
              >
                <span className="pim-review__k">{r.label}</span>
                <span className="pim-review__cell">
                  {r.isPhone && phoneCell !== null ? (
                    phoneCell
                  ) : (
                    <>
                      {fixable ? (
                        /* W-3: the confirmed value is tap-to-edit in place --
                           tapping the value opens the SAME bounded editor the
                           Fix button opens (one state, two doors). */
                        <button
                          type="button"
                          className="pim-review__v pim-review__v--tap"
                          disabled={busy}
                          onClick={() => onToggleFix(r.field)}
                          data-testid={`intake-value-${r.field}`}
                        >
                          {r.isPhone
                            ? (r.hasValue ? 'On file' : 'Add it below')
                            : (r.displayValue ?? '-')}
                        </button>
                      ) : (
                        <span className="pim-review__v">
                          {r.isPhone
                            ? (r.hasValue ? 'On file' : 'Add it below')
                            : (r.displayValue ?? '-')}
                        </span>
                      )}
                      <span className="pim-review__src">Source: {r.sourceLabel}</span>
                    </>
                  )}
                </span>
                {fixable ? (
                  <button
                    type="button"
                    className="pim-fix"
                    disabled={busy}
                    aria-expanded={open}
                    onClick={() => onToggleFix(r.field)}
                    data-testid={`intake-fix-${r.field}`}
                  >
                    {open ? 'Keep as is' : 'Fix'}
                  </button>
                ) : null}
              </div>
              {open ? (
                <div className="pim-fixbody" data-testid={`intake-fixbody-${r.field}`}>
                  {fixEditor(r)}
                </div>
              ) : null}
            </div>
          );
        })}
        {ageText !== null ? (
          <div className="pim-review__row" data-testid="intake-detail-age">
            <span className="pim-review__k">Age</span>
            <span className="pim-review__cell">
              <span className="pim-review__v" data-testid="patient-intake-age">
                {ageText}
              </span>
            </span>
          </div>
        ) : null}
        {/* FD-35 (RECEPTION-FIX-AND-POLISH-BATCH-V1): the MR alias rows and
            the "No external MR on file" line are OPS vocabulary -- they
            never render on the patient's own surface. The desk reads MR
            aliases on its own screens (patient record / checkpoint). */}
      </div>
    </section>
  );
}


/**
 * THE ONE CHOICE-CHIP ROW (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1, D5).
 *
 * The `ac-tristate` chip pattern the registration form has always used,
 * extracted so the doctor's relocated intake block renders the SAME control
 * instead of a second copy of it (UX LAW 3: one visual language; a parallel
 * primitive is a defect, not a style choice). Pure + SSR-renderable like every
 * other screen in this file.
 *
 * It lives HERE, in an already-grandfathered raw-<button> file, on purpose:
 * the design-system guard forbids a NEW file from rendering raw buttons, and
 * a chip is not one of the three action tiers (PrimaryAction / SecondaryAction
 * / QuietAction) -- it is a selection control. Promoting it to a registered
 * ui/ primitive is a design-system package, named in the run record, not a
 * side effect of this one.
 */
export function IntakeChoiceChips({
  ariaLabel,
  options,
  isSelected,
  onPick,
  disabled,
  testId,
}: {
  readonly ariaLabel: string;
  readonly options: ReadonlyArray<{ readonly code: string; readonly label: string }>;
  readonly isSelected: (code: string) => boolean;
  readonly onPick: (code: string) => void;
  readonly disabled?: boolean;
  /** `${testId}-${code}` per chip; omitted when absent. */
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="ac-tristate" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.code}
          type="button"
          className={
            isSelected(o.code)
              ? 'ac-tristate__option ac-tristate__option--on'
              : 'ac-tristate__option'
          }
          aria-pressed={isSelected(o.code)}
          disabled={disabled}
          onClick={() => onPick(o.code)}
          {...(testId ? { 'data-testid': `${testId}-${o.code}` } : {})}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** F9 (AUG-12-WALK-BLOCKERS-V1): the privacy + communication-preference
 *  block, moved OUT of the details card to sit adjacent to the single
 *  submit. Same honest posture as before (FD-34): the two labeled lines,
 *  named disclosures, no dead controls (LES-016) -- persistence of a changed
 *  promotional preference still has no lawful storage owner. */
export function IntakeConsentBlock({
  consent,
  compact = false,
}: {
  readonly consent: IntakeConsentPosture;
  readonly compact?: boolean;
}): JSX.Element {
  if (compact) return <div className="pim-policy pim-policy--compact" data-testid="patient-intake-consent">
    <details className="pim-policy__details" data-testid="intake-privacy-link">
      <summary className="pim-policy__more">Privacy</summary>
      <p data-testid="intake-consent-service">{consent.serviceLabel}: {consent.serviceAllowed === false ? 'off' : 'on'}.</p>
      <p className="pim-policy__note">{consent.serviceNote}</p>
    </details>
    <details className="pim-policy__details" data-testid="intake-comm-prefs-link">
      <summary className="pim-policy__more">Communication preferences</summary>
      <p data-testid="intake-consent-promotional">{consent.promotionalLabel}: {consent.promotionalAllowed === true ? 'yes' : consent.promotionalAllowed === false ? 'no' : 'your choice - tell the receptionist'}.</p>
      <p className="pim-policy__note">{consent.promotionalNote}</p>
    </details>
  </div>;
  return (
    <div className="pim-policy pim-policy--foot" data-testid="patient-intake-consent">
      <p className="pim-policy__line" data-testid="intake-consent-service">
        <span>
          {consent.serviceLabel}: {consent.serviceAllowed === false ? 'off' : 'on'}.
        </span>
      </p>
      <p className="pim-policy__line" data-testid="intake-consent-promotional">
        <span>
          {consent.promotionalLabel}:{' '}
          {consent.promotionalAllowed === true
            ? 'yes'
            : consent.promotionalAllowed === false
              ? 'no'
              : 'your choice - tell the receptionist'}
          .
        </span>
      </p>
      <details className="pim-policy__details" data-testid="intake-privacy-link">
        <summary className="pim-policy__more">Privacy</summary>
        <p className="pim-policy__note">{consent.serviceNote}</p>
      </details>
      <details
        className="pim-policy__details"
        data-testid="intake-comm-prefs-link"
      >
        <summary className="pim-policy__more">Communication preferences</summary>
        <p className="pim-policy__note">{consent.promotionalNote}</p>
      </details>
    </div>
  );
}

export { fieldLabel };
